import React, { useState, useCallback, useMemo } from 'react';
import { View, Text } from 'react-native';
import { Button } from 'react-native-paper';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramDuctFrictionLoss } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { ductFrictionLoss, DuctShape } from '../../../lib/calcs/duct';
import { formatNumber, formatWithUnit } from '../../../utils/format';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.ductFrictionLoss.v2';

type ShapeUi = 'round' | 'rectangular';

type Fields = {
  cfm: string;
  lengthFt: string;
  diameterIn: string;
  widthIn: string;
  heightIn: string;
  roughnessMm: string;
  temperatureC: string;
  // bidirectional scaling group: ΔP_total = frictionRate × L / 100
  frictionRate: string; // pressure per 100 length units, in display pressure unit per 100 display length
  deltaPTotal: string;  // in display pressure unit
};
const EMPTY: Fields = {
  cfm: '',
  lengthFt: '100',
  diameterIn: '',
  widthIn: '',
  heightIn: '',
  roughnessMm: '0.09',
  temperatureC: '20',
  frictionRate: '',
  deltaPTotal: '',
};

type Result = { deltaPInWg: number; deltaPPa: number; velocityMPerS: number; reynolds: number; frictionFactor: number };

export default function DuctFrictionLossScreen() {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [shape, setShape] = useState<ShapeUi>('round');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [solvedFor, setSolvedFor] = useState<Set<keyof Fields>>(() => new Set());
  const { unit, toImp, fromImp } = useUnits();

  const ACCENT = '#22c55e';

  const set = useCallback((patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch })), [setFields]);

  // Bidirectional solve for {L, frictionRate, ΔP_total}. Relation: ΔP = frictionRate × L / 100.
  const solveScalingGroup = useCallback((): boolean => {
    const core = [fields.lengthFt, fields.frictionRate, fields.deltaPTotal];
    const filled = core.filter((v) => v !== '').length;
    if (filled < 2) { setError('Scaling: fill any 2 of {L, friction rate, ΔP_total} to solve the third.'); return true; }
    if (filled === 3) { setError('Scaling: all 3 values are filled — clear one to solve for it.'); return true; }

    const L = toImp(fields.lengthFt, 'length'); // feet
    const fr = toImp(fields.frictionRate, 'pressure'); // inWg per 100 ft
    const dp = toImp(fields.deltaPTotal, 'pressure'); // inWg

    if (fields.deltaPTotal === '') {
      if (!(L >= 0) || !(fr >= 0)) { setError('L and friction rate must be ≥ 0.'); return true; }
      setSolvedFor(new Set(['deltaPTotal']));
      set({ deltaPTotal: fromImp(fr * L / 100, 'pressure', 4) });
      return true;
    } else if (fields.frictionRate === '') {
      if (!(L > 0) || !(dp >= 0)) { setError('L must be > 0; ΔP ≥ 0.'); return true; }
      setSolvedFor(new Set(['frictionRate']));
      set({ frictionRate: fromImp(dp * 100 / L, 'pressure', 4) });
      return true;
    } else if (fields.lengthFt === '') {
      if (!(fr > 0) || !(dp >= 0)) { setError('Friction rate must be > 0; ΔP ≥ 0.'); return true; }
      setSolvedFor(new Set(['lengthFt']));
      set({ lengthFt: fromImp(dp * 100 / fr, 'length', 1) });
      return true;
    }
    return false;
  }, [fields, toImp, fromImp, set]);

  const calculate = useCallback(() => {
    setError(null);

    // If duct-geometry inputs are provided, run the full chart calc and populate frictionRate + ΔP_total.
    const hasGeom =
      fields.cfm !== '' &&
      ((shape === 'round' && fields.diameterIn !== '') ||
        (shape === 'rectangular' && fields.widthIn !== '' && fields.heightIn !== ''));

    if (hasGeom) {
      const q = toImp(fields.cfm, 'flow');
      const l = toImp(fields.lengthFt, 'length');
      const eps = Number(fields.roughnessMm);
      const tC = Number(fields.temperatureC);

      if (!Number.isFinite(q) || q < 0) { setError('Airflow must be ≥ 0'); return; }
      if (!Number.isFinite(l) || l <= 0) { setError('Length must be > 0 to compute friction rate.'); return; }
      if (!Number.isFinite(eps) || eps < 0) { setError('Roughness must be ≥ 0'); return; }
      if (!Number.isFinite(tC)) { setError('Temperature must be a number'); return; }

      try {
        const shapeCalc: DuctShape = shape;
        const dIn = toImp(fields.diameterIn, 'diameter');
        const wIn = toImp(fields.widthIn, 'diameter');
        const hIn = toImp(fields.heightIn, 'diameter');
        const r = ductFrictionLoss({
          cfm: q,
          lengthFt: l,
          shape: shapeCalc,
          diameterIn: shapeCalc === 'round' ? dIn : undefined,
          widthIn: shapeCalc === 'rectangular' ? wIn : undefined,
          heightIn: shapeCalc === 'rectangular' ? hIn : undefined,
          roughnessMm: eps,
          temperatureC: tC,
        });
        setResult({ deltaPInWg: r.deltaPInWg, deltaPPa: r.deltaPPa, velocityMPerS: r.velocityMPerS, reynolds: r.reynolds, frictionFactor: r.frictionFactor });
        // Auto-fill the scaling group fields from the chart calc.
        const frInWgPer100 = r.deltaPInWg * 100 / l;
        setSolvedFor(new Set(['frictionRate', 'deltaPTotal']));
        set({
          frictionRate: fromImp(frInWgPer100, 'pressure', 4),
          deltaPTotal: fromImp(r.deltaPInWg, 'pressure', 4),
        });
        return;
      } catch {
        setError('Unable to calculate. Check duct dimensions and inputs.');
        return;
      }
    }

    // Otherwise run the bidirectional scaling solve.
    setResult(null);
    solveScalingGroup();
  }, [fields, shape, toImp, fromImp, set, solveScalingGroup]);

  const clearAll = useCallback(() => {
    setError(null);
    void clearPersisted();
    setResult(null);
    setSolvedFor(new Set());
  }, [clearPersisted]);

  const resultLines = useMemo(() => result
    ? [
        { label: 'ΔP', value: `${fromImp(result.deltaPInWg, 'pressure', 4)} ${unit('pressure')}`, emphasis: true as const },
        { label: 'Velocity', value: formatWithUnit(result.velocityMPerS, 'm/s', { decimals: 2 }) },
        { label: 'Re', value: formatNumber(result.reynolds, { decimals: 0, useGrouping: true }) },
        { label: 'f', value: formatNumber(result.frictionFactor, { decimals: 4 }) },
      ]
    : ([
        fields.frictionRate && { label: 'Friction rate', value: fields.frictionRate, unit: `${unit('pressure')} / 100 ${unit('length')}`, emphasis: solvedFor.has('frictionRate') },
        fields.lengthFt && { label: 'Length', value: fields.lengthFt, unit: unit('length'), emphasis: solvedFor.has('lengthFt') },
        fields.deltaPTotal && { label: 'ΔP total', value: fields.deltaPTotal, unit: unit('pressure'), emphasis: solvedFor.has('deltaPTotal') },
      ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[]), [result, fields, unit, solvedFor, fromImp]);

  return (
    <CalcShell
      title="Duct Friction Loss"
      subtitle="Darcy–Weisbach pressure loss, plus bidirectional {L, friction rate, ΔP_total} scaling"
      formula={
        <>
          <FormulaLine>ΔP = f · (L/Dh) · (ρ·V²/2)</FormulaLine>
          <FormulaLine>ΔP_total = friction rate × L / 100</FormulaLine>
          <FormulaNote>ASHRAE Fundamentals — Darcy–Weisbach; Swamee–Jain friction factor</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={resultLines && resultLines.length ? resultLines : null}
      copyFooter="Formula: Darcy–Weisbach duct friction loss"
      diagram={<DiagramDuctFrictionLoss />}
      outputSlots={{
        cfm: fields.cfm ? toImp(fields.cfm, 'flow') : undefined,
        duct_diameter_in: shape === 'round' && fields.diameterIn ? toImp(fields.diameterIn, 'diameter') : undefined,
        duct_width_in: shape === 'rectangular' && fields.widthIn ? toImp(fields.widthIn, 'diameter') : undefined,
        duct_height_in: shape === 'rectangular' && fields.heightIn ? toImp(fields.heightIn, 'diameter') : undefined,
        static_pressure_iwg: fields.deltaPTotal
          ? toImp(fields.deltaPTotal, 'pressure')
          : result
            ? result.deltaPInWg
            : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.cfm != null && !fields.cfm) patch.cfm = fromImp(values.cfm, 'flow', 2);
        if (values.duct_diameter_in != null && !fields.diameterIn) {
          patch.diameterIn = fromImp(values.duct_diameter_in, 'diameter', 2);
          setShape('round');
        }
        if (values.duct_width_in != null && !fields.widthIn) {
          patch.widthIn = fromImp(values.duct_width_in, 'diameter', 2);
          setShape('rectangular');
        }
        if (values.duct_height_in != null && !fields.heightIn) {
          patch.heightIn = fromImp(values.duct_height_in, 'diameter', 2);
          setShape('rectangular');
        }
        if (values.static_pressure_iwg != null && !fields.deltaPTotal) {
          patch.deltaPTotal = fromImp(values.static_pressure_iwg, 'pressure', 4);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
        <Button
          mode={shape === 'round' ? 'contained' : 'outlined'}
          onPress={() => { setShape('round'); setResult(null); }}
          style={[{ flex: 1 }, shape === 'round' ? { backgroundColor: ACCENT } : null]}
        >
          Round
        </Button>
        <Button
          mode={shape === 'rectangular' ? 'contained' : 'outlined'}
          onPress={() => { setShape('rectangular'); setResult(null); }}
          style={[{ flex: 1 }, shape === 'rectangular' ? { backgroundColor: ACCENT } : null]}
        >
          Rectangular
        </Button>
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Airflow" value={fields.cfm} onChangeText={(v) => set({ cfm: v })} rightText={unit('flow')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      {shape === 'round' ? (
        <View style={{ marginBottom: 12 }}>
          <NumericField label="Diameter" value={fields.diameterIn} onChangeText={(v) => set({ diameterIn: v })} rightText={unit('diameter')} maxDecimals={2} onSubmitEditing={calculate} />
        </View>
      ) : (
        <>
          <View style={{ marginBottom: 12 }}>
            <NumericField label="Width" value={fields.widthIn} onChangeText={(v) => set({ widthIn: v })} rightText={unit('diameter')} maxDecimals={2} onSubmitEditing={calculate} />
          </View>
          <View style={{ marginBottom: 12 }}>
            <NumericField label="Height" value={fields.heightIn} onChangeText={(v) => set({ heightIn: v })} rightText={unit('diameter')} maxDecimals={2} onSubmitEditing={calculate} />
          </View>
        </>
      )}
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Roughness" value={fields.roughnessMm} onChangeText={(v) => set({ roughnessMm: v })} rightText="mm" maxDecimals={3} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 16 }}>
        <NumericField label="Temperature" value={fields.temperatureC} onChangeText={(v) => set({ temperatureC: v })} rightText="°C" maxDecimals={1} onSubmitEditing={calculate} />
      </View>

      <Text style={{ fontWeight: '600', marginBottom: 8 }}>Scaling — fill any 2 of 3</Text>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Length" value={fields.lengthFt} onChangeText={(v) => set({ lengthFt: v })} rightText={unit('length')} maxDecimals={1} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Friction rate (${unit('pressure')} / 100 ${unit('length')})`}
          value={fields.frictionRate}
          onChangeText={(v) => set({ frictionRate: v })}
          rightText={`${unit('pressure')}/100${unit('length')}`}
          maxDecimals={4}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`ΔP total (${unit('pressure')})`}
          value={fields.deltaPTotal}
          onChangeText={(v) => set({ deltaPTotal: v })}
          rightText={unit('pressure')}
          maxDecimals={4}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
}
